import { FilterNames } from '@rialtic/types'
import { createSharedComposable } from '@vueuse/core'
import { defu } from 'defu'
import { computed } from '#imports'
import { type UseRouteQueryOptions, useRouteQuery } from './sharedComposables'

type UrlParamsState = Record<string, string[] | string>

const DELIMITER = '|'
const SINGLE_VALUE_PARAMS: string[] = [
  'insight_mode',
  'selected_edit',
  'line_date_of_service_start',
  'line_date_of_service_end',
  'rid',
]
export const DEFAULT_QUERY_PARAMS = [
  'lob',
  'start_date',
  'end_date',
  'preset',
  FilterNames.SORT_BY,
]

const arrayToPipes = (array: string | string[]) =>
  Array.isArray(array) ? array.join(DELIMITER) : array

/**
 * Splits a pipe delimited URL param into its values, trimmed and without empties
 * @param (string | string[]) - Raw param value, already an array when the router parsed one
 * @returns (string[]) - Normalized values
 */
export const pipesToArray = (pipes: string | string[]) => {
  if (!pipes) return []

  const values = Array.isArray(pipes) ? pipes : pipes.split(DELIMITER)

  return values.map((value) => value.trim()).filter(Boolean)
}

const paramsToPipes = (params: Record<string, string | string[]>) =>
  Object.entries(params).reduce<Record<string, string>>(
    (state, [key, value]) => {
      state[key] = arrayToPipes(value)
      return state
    },
    {},
  )

/**
 * Processes parameters and structures output data according to business rules
 * @param (Record<string, string>) - URL parameters
 * @returns (Record<string, string[] | string | boolean>) - Processed parameters for state storage
 */
const processedParams = (params: Record<string, string>) =>
  Object.entries(params).reduce<Record<string, string[] | string | boolean>>(
    (state, [key, value]) => {
      if (value == null) {
        return state
      }

      const valueToLower = value.toLowerCase()

      // TODO: determine if single value logic is correct when not boolean
      if (value && SINGLE_VALUE_PARAMS.includes(key))
        state[key] =
          valueToLower === 'true' || valueToLower === 'false'
            ? JSON.parse(valueToLower)
            : pipesToArray(value)
      else if (value) state[key] = pipesToArray(value)

      return state
    },
    {},
  )

// `createSharedComposable` caches the arg(s) passed to the first call of the composable
// this silently drops arg(s) from new calls which can lead to unexpected behavior
export const useUrlParamsState = createSharedComposable(
  (options?: UseRouteQueryOptions<UrlParamsState>) => {
    const opts = defu(
      {
        initialValue: paramsToPipes(options?.initialValue ?? {}),
      },
      options,
    )

    const activeUrlParams = useRouteQuery('url-query-state', opts)

    /**
     * A list of normalized URL parameters
     * @see {@link activeUrlParams}
     * @see {@link processedParams}
     * @returns (Record<string, string[] | string | boolean>) - Normalized list of URL parameters
     */
    const normalizedParams = computed(() => {
      const params = processedParams(activeUrlParams)

      if (Object.hasOwn(params, FilterNames.DOS_RANGE)) {
        const range = params[FilterNames.DOS_RANGE]

        if (Array.isArray(range) && range[0]) {
          params[FilterNames.LINE_DOS_START] = range[0]

          if (range[1]) {
            params[FilterNames.LINE_DOS_END] = range[1]
          }
        }
      }

      return params
    })

    return {
      activeUrlParams,
      clearUrlParams(keep: string[] = []) {
        const keysToKeep = [...keep, ...DEFAULT_QUERY_PARAMS]

        for (const key of Object.keys(activeUrlParams)) {
          if (!keysToKeep.includes(key)) delete activeUrlParams[key]
        }
      },

      deleteUrlParam(param: string) {
        delete activeUrlParams[param]
      },

      patchUrlParams(
        updates: Record<string, string | string[]>,
        overwrite = false,
      ) {
        const params: Record<string, string> = {}

        for (const [key, value] of Object.entries(updates)) {
          params[key] = arrayToPipes(value)
        }

        if (overwrite) {
          Object.assign(activeUrlParams, params)
        } else {
          Object.assign(activeUrlParams, { ...activeUrlParams, ...params })
        }
      },

      setUrlParams(param: string, value: string | string[]) {
        activeUrlParams[param] = arrayToPipes(value)
      },

      setUrlSearchParams(params: URLSearchParams) {
        for (const param of params.keys()) {
          activeUrlParams[param] = params.get(param)!
        }
      },

      getUrlParams: (param: string) => pipesToArray(activeUrlParams[param]),

      normalizedParams,
    }
  },
)
